import ExifReader from 'exifreader';
import type { PhotoExif } from '../types';

type ExifTag = {
  description?: string;
  value?: unknown;
};

type ExifTags = Record<string, ExifTag | undefined>;

function tag(tags: ExifTags, key: string): string | undefined {
  const value = tags[key];
  if (!value) return undefined;
  if (typeof value.description === 'string' && value.description.trim()) return value.description.trim();
  if (typeof value.value === 'string') return value.value.trim();
  if (typeof value.value === 'number') return String(value.value);
  return undefined;
}

function gpsDecimal(ref: string | undefined, raw: ExifTag | undefined): number | undefined {
  const value = raw?.value;
  if (!Array.isArray(value) || value.length < 3) return undefined;
  const [degrees, minutes, seconds] = value.map((part) => {
    if (Array.isArray(part) && part.length === 2) return Number(part[0]) / Number(part[1]);
    return Number(part);
  });
  if ([degrees, minutes, seconds].some((part) => Number.isNaN(part))) return undefined;
  const sign = ref === 'S' || ref === 'W' ? -1 : 1;
  return sign * (degrees + minutes / 60 + seconds / 3600);
}

export async function parseExif(file: File): Promise<PhotoExif> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const tags = (await ExifReader.load(arrayBuffer)) as ExifTags;
    const make = tag(tags, 'Make');
    const model = tag(tags, 'Model');
    const latitude = gpsDecimal(tag(tags, 'GPSLatitudeRef'), tags.GPSLatitude);
    const longitude = gpsDecimal(tag(tags, 'GPSLongitudeRef'), tags.GPSLongitude);

    return {
      camera: [make, model].filter(Boolean).join(' ') || undefined,
      lens: tag(tags, 'LensModel') ?? tag(tags, 'Lens') ?? tag(tags, 'LensInfo'),
      shutter: tag(tags, 'ExposureTime') ?? tag(tags, 'ShutterSpeedValue'),
      aperture: tag(tags, 'FNumber') ?? tag(tags, 'ApertureValue'),
      iso: tag(tags, 'ISOSpeedRatings') ?? tag(tags, 'PhotographicSensitivity'),
      focalLength: tag(tags, 'FocalLength'),
      date: tag(tags, 'DateTimeOriginal') ?? tag(tags, 'CreateDate') ?? tag(tags, 'DateTime'),
      location:
        latitude !== undefined && longitude !== undefined
          ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
          : undefined
    };
  } catch {
    return {};
  }
}
